import { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../layout/header";
import { getAllContactUsURL } from "../libraries/apiUrl";
import { _get } from "../libraries/fetch";

const TABS = [{ id: "requested-feature", label: "Requested Feature" }];
const MOBILE_APP_SOURCE = "Mobile App";

const TABLE_HEADINGS = [
  "Sr No.",
  "Created Date",
  "Name",
  "Phone",
  "Email",
  "Gym Name",
  "Message",
  "Source"
];

function FeatureRequestsPage() {
  const [activeTab, setActiveTab] = useState("requested-feature");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchContactUs = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await _get(getAllContactUsURL(), { useCache: false });

      if (response == null) {
        throw new Error("Failed to load contact us requests.");
      }

      if (response?.success === false || response?.status === false) {
        throw new Error(response.message || "Failed to load contact us requests.");
      }

      const nextItems = extractContactUsItems(response)
        .map(normalizeContactUsItem)
        .filter((item) => isMobileAppSource(item.source));

      setItems(nextItems);
    } catch (err) {
      setError(err?.message || "Failed to load contact us requests. Please try again.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContactUs();
  }, [fetchContactUs]);

  const rows = useMemo(() => items, [items]);

  return (
    <div className="main">
      <Header />
      <main style={pageStyle}>
        <div className="branchMenu" aria-label="Mobile App tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={activeTab === tab.id ? "active" : ""}
              style={activeTab === tab.id ? selectedTabStyle : undefined}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === "requested-feature" && (
          <div className="contentArea" style={tabContentStyle}>
            <div style={actionsStyle}>
              <button type="button" onClick={fetchContactUs} disabled={loading} style={refreshButtonStyle}>
                {loading ? "Loading..." : "Refresh"}
              </button>
            </div>
            <div style={tableShellStyle}>
              {loading ? (
                <div style={emptyStyle}>Loading feature requests...</div>
              ) : error ? (
                <div style={errorStyle}>{error}</div>
              ) : rows.length === 0 ? (
                <div style={emptyStyle}>No feature requests found.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr style={tableHeadRowStyle}>
                        {TABLE_HEADINGS.map((heading) => (
                          <th key={heading} style={tableHeaderStyle}>
                            {heading}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((item, index) => (
                        <tr
                          key={item.id || `${item.email}-${item.createdDate}-${index}`}
                          style={{
                            borderBottom: "1px solid #333",
                            backgroundColor: index % 2 === 0 ? "#1a1d23" : "#20232b"
                          }}
                        >
                          <td style={tableCellStyle}>{index + 1}</td>
                          <td style={tableCellStyle}>{formatDate(item.createdDate)}</td>
                          <td style={tableCellStyle}>{item.name}</td>
                          <td style={tableCellStyle}>{item.phone}</td>
                          <td style={tableCellStyle}>{item.email}</td>
                          <td style={tableCellStyle}>{item.subject}</td>
                          <td style={messageCellStyle}>{item.message}</td>
                          <td style={tableCellStyle}>{item.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function extractContactUsItems(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (!response || typeof response !== "object") {
    return [];
  }

  const listKeys = [
    "data",
    "contactUs",
    "contact-us",
    "contacts",
    "requests",
    "response",
    "result",
    "results",
    "records",
    "items",
    "docs"
  ];

  for (const key of listKeys) {
    const value = response[key];

    if (Array.isArray(value)) {
      return value;
    }

    const nestedItems = extractContactUsItems(value);
    if (nestedItems.length > 0) {
      return nestedItems;
    }
  }

  return [];
}

function normalizeContactUsItem(item) {
  return {
    id: getValue(item, ["_id", "id"]),
    createdDate: getValue(item, ["createdDate", "createdAt", "created_at", "date", "submittedAt"]),
    name: formatValue(getValue(item, ["name", "fullName", "clientName", "customerName"])),
    phone: formatValue(getValue(item, ["number", "phone", "phoneNumber", "contactNumber", "mobile"])),
    email: formatValue(getValue(item, ["email", "emailAddress"])),
    subject: formatValue(getValue(item, ["subject", "gymName", "title"])),
    message: formatValue(getValue(item, ["requestedFeature", "message", "feature", "description", "details", "comment"])),
    source: formatValue(getValue(item, ["source", "Source", "origin"]))
  };
}

function isMobileAppSource(source) {
  return String(source || "").trim().toLowerCase() === MOBILE_APP_SOURCE.toLowerCase();
}

function getValue(source, keys) {
  if (!source || typeof source !== "object") {
    return "";
  }

  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null && source[key] !== "") {
      return source[key];
    }
  }

  return "";
}

function formatValue(value) {
  if (value === undefined || value === null || value === "") {
    return "N/A";
  }

  if (Array.isArray(value)) {
    return value.map(formatValue).join(", ");
  }

  if (typeof value === "object") {
    return value.name || value.title || value.label || value._id || value.id || JSON.stringify(value);
  }

  return String(value);
}

function formatDate(dateString) {
  if (!dateString || dateString === "N/A") {
    return "N/A";
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return formatValue(dateString);
  }

  return date.toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

const pageStyle = {
  minHeight: "calc(100vh - 80px)",
  padding: "clamp(16px, 3vw, 24px) clamp(12px, 3vw, 20px)",
  maxWidth: "100%",
  boxSizing: "border-box",
  background: "linear-gradient(135deg, #1e2127 0%, #2a2d35 100%)"
};

const selectedTabStyle = {
  color: "#000"
};

const tabContentStyle = {
  padding: "clamp(14px, 3vw, 24px)",
  maxWidth: "100%",
  boxSizing: "border-box"
};

const actionsStyle = {
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: "16px"
};

const refreshButtonStyle = {
  padding: "10px 18px",
  fontSize: "14px",
  fontWeight: 600,
  backgroundColor: "#187718",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer"
};

const tableShellStyle = {
  overflowX: "auto",
  border: "1px solid #3a3f4b",
  borderRadius: "10px",
  backgroundColor: "#1a1d23",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)"
};

const tableStyle = {
  width: "100%",
  minWidth: "920px",
  borderCollapse: "collapse",
  fontSize: "14px"
};

const tableHeadRowStyle = {
  backgroundColor: "#102235",
  borderBottom: "2px solid #187718"
};

const tableHeaderStyle = {
  padding: "14px 12px",
  color: "#fff",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.3px",
  textAlign: "left",
  whiteSpace: "nowrap"
};

const tableCellStyle = {
  padding: "12px",
  color: "#fff",
  verticalAlign: "middle"
};

const messageCellStyle = {
  ...tableCellStyle,
  minWidth: "220px",
  lineHeight: 1.35
};

const emptyStyle = {
  padding: "42px 20px",
  color: "#9ca3af",
  textAlign: "center"
};

const errorStyle = {
  padding: "18px",
  margin: "20px",
  borderRadius: "8px",
  backgroundColor: "rgba(213, 23, 23, 0.15)",
  border: "1px solid rgba(213, 23, 23, 0.45)",
  color: "#ff9b9b",
  fontWeight: 600
};

export default FeatureRequestsPage;
